import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Briefcase } from 'lucide-react';
import api from '../../services/api';

export interface WorkFieldSelection {
  fieldIds: number[];
  tipasIds: number[];
  positionIds: number[];
}

interface TaxRef { id: number; name: string; }
interface TaxPosition { id: number; name: string; tipas_id?: number | null; }

interface Props {
  value: WorkFieldSelection;
  onChange: (next: WorkFieldSelection) => void;
  /** Emitted with human-readable labels of the current selection (fields → types →
   *  positions), so the caller can render chips without re-resolving names. */
  onLabelsChange?: (labels: string[]) => void;
}

/**
 * Shared "Darbo sritys" cascade picker (work field → position type → position),
 * backed by the relational taxonomy. Produces id-based selections that match the
 * canonical job search vocabulary. Used by the saved-search and prenumerata modals.
 */
const WorkFieldCascadePicker: React.FC<Props> = ({ value, onChange, onLabelsChange }) => {
  const [fields, setFields] = useState<TaxRef[]>([]);
  const [tipai, setTipai] = useState<TaxRef[]>([]);
  const [positionsByField, setPositionsByField] = useState<Record<number, TaxPosition[]>>({});
  const [positionNames, setPositionNames] = useState<Record<number, string>>({});
  const [fieldSearch, setFieldSearch] = useState('');
  const [posSearch, setPosSearch] = useState('');
  const lastLabels = useRef<string>('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/taxonomy');
        setFields(res.data?.data?.darboSritys || []);
        setTipai(res.data?.data?.tipai || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const ensurePositions = useCallback((fieldId: number) => {
    setPositionsByField(prev => {
      if (prev[fieldId]) return prev;
      api.get(`/taxonomy/fields/${fieldId}/positions`)
        .then(res => setPositionsByField(p => ({
          ...p,
          [fieldId]: (res.data?.data || []).map((x: any) => ({
            id: x.id,
            name: x.name,
            tipas_id: x.tipas_id != null ? Number(x.tipas_id) : null,
          })),
        })))
        .catch(() => {});
      return prev;
    });
  }, []);

  // Preload positions for already-selected fields (e.g. when editing an existing
  // saved search) so their chips/names resolve and narrowing works immediately.
  useEffect(() => {
    value.fieldIds.forEach(id => ensurePositions(id));
  }, [value.fieldIds, ensurePositions]);

  // Resolve names for selected positions we don't yet know (restored selections).
  useEffect(() => {
    const known = (id: number) =>
      positionNames[id] || Object.values(positionsByField).some(arr => arr.some(p => p.id === id));
    value.positionIds.filter(id => !known(id)).forEach(async id => {
      try {
        const res = await api.get(`/taxonomy/positions/${id}`);
        const name = res.data?.data?.name;
        if (name) setPositionNames(prev => ({ ...prev, [id]: name }));
      } catch { /* leave unresolved */ }
    });
  }, [value.positionIds, positionsByField, positionNames]);

  // Report resolved labels upward whenever the selection or taxonomy data changes.
  useEffect(() => {
    if (!onLabelsChange) return;
    const nameFromPositions = (id: number) =>
      positionNames[id] ||
      Object.values(positionsByField).flat().find(p => p.id === id)?.name;
    const labels = [
      ...value.fieldIds.map(id => fields.find(f => f.id === id)?.name),
      ...value.tipasIds.map(id => tipai.find(t => t.id === id)?.name),
      ...value.positionIds.map(nameFromPositions),
    ].filter(Boolean) as string[];
    const key = labels.join('|');
    if (key !== lastLabels.current) {
      lastLabels.current = key;
      onLabelsChange(labels);
    }
  }, [value, fields, tipai, positionsByField, positionNames, onLabelsChange]);

  const toggle = (key: keyof WorkFieldSelection, id: number) => {
    const cur = value[key];
    const isAdding = !cur.includes(id);
    const next: WorkFieldSelection = {
      ...value,
      [key]: isAdding ? [...cur, id] : cur.filter(v => v !== id),
    };
    if (key === 'fieldIds' && isAdding) ensurePositions(id);
    onChange(next);
  };

  return (
    <div className="work-field-layout">
      <div className="wf-card work-field-col">
        <h4><Briefcase size={16} /> Darbo sritys</h4>
        <input
          type="text"
          placeholder="Įveskite darbo sritį"
          value={fieldSearch}
          onChange={(e) => setFieldSearch(e.target.value)}
          className="filter-input"
        />
        <div className="checkbox-list wf-two-col">
          {fields
            .filter(f => !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase()))
            .map(f => (
              <label key={f.id} className="checkbox-label">
                <input type="checkbox" checked={value.fieldIds.includes(f.id)} onChange={() => toggle('fieldIds', f.id)} />
                <span className="filter-option-label">{f.name}</span>
              </label>
            ))}
        </div>
      </div>
      <div className="work-field-right">
        <div className="wf-card position-type-col">
          <h4><Briefcase size={16} /> Pareigybių tipai</h4>
          <div className="checkbox-list position-type-grid">
            {tipai.map(t => (
              <label key={t.id} className="checkbox-label">
                <input type="checkbox" checked={value.tipasIds.includes(t.id)} onChange={() => toggle('tipasIds', t.id)} />
                <span className="filter-option-label">{t.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="wf-card positions-col">
          <h4><Briefcase size={16} /> Pareigybės pagal pasirinktas darbo sritis</h4>
          {value.fieldIds.length === 0 ? (
            <div className="no-results-hint">Pasirinkite bent vieną darbo sritį</div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Įveskite pareigybę"
                value={posSearch}
                onChange={(e) => setPosSearch(e.target.value)}
                className="filter-input"
              />
              <div className="checkbox-list">
                {(() => {
                  const seen = new Set<number>();
                  const positions: TaxPosition[] = [];
                  for (const fid of value.fieldIds) {
                    for (const p of positionsByField[fid] || []) {
                      if (seen.has(p.id)) continue;
                      seen.add(p.id);
                      if (value.tipasIds.length > 0 && !(p.tipas_id != null && value.tipasIds.includes(p.tipas_id))) continue;
                      if (!posSearch || p.name.toLowerCase().includes(posSearch.toLowerCase())) positions.push(p);
                    }
                  }
                  positions.sort((a, b) => a.name.localeCompare(b.name, 'lt'));
                  if (positions.length === 0) return <div className="no-results-hint">Pareigybių nerasta</div>;
                  return positions.map(p => (
                    <label key={p.id} className="checkbox-label">
                      <input type="checkbox" checked={value.positionIds.includes(p.id)} onChange={() => toggle('positionIds', p.id)} />
                      <span className="filter-option-label">{p.name}</span>
                    </label>
                  ));
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkFieldCascadePicker;
